import * as queryString from 'query-string';
import { Dispatch } from 'redux';
import { FacebookStaticSDK } from '../interfaces/facebook';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer, { makeActionKey } from './CrudActionContainer';

const ENTITY = 'FACEBOOK_SDK';
export const CONNECT_FAIL_MESSAGE = 'Cannot reach Facebook SDK';

declare global {
  interface Window {
    fbAsyncInit: () => any;
    FB: FacebookStaticSDK;
  }
}

type FBLoginAuthType = 'rerequest' | 'reauthenticate' | 'reauthorize';

class FacebookSDK extends CrudActionContainer {
  chatLoaded = false;
  FB?: FacebookStaticSDK;
  appId?: number;

  ACTIONS = {
    SET_IS_INITIALIZED: makeActionKey(ENTITY, 'SET_IS_INITIALIZED'),
    SET_CUSTOMER_CHAT_LOADED: makeActionKey(ENTITY, 'SET_CUSTOMER_CHAT_LOADED'),
  };

  constructor() {
    super(ENTITY);
  }

  // Not sure if should put this here as it technically is not a action
  // but needs some state from init
  getFacebookSDK(pollAttempts: number = 30): Promise<FacebookStaticSDK> {
    if (this.FB) {
      return Promise.resolve(this.FB);
    }

    let pollNumber = 0;

    return new Promise((resolve, reject) => {
      const intervalId = setInterval(() => {
        if (this.FB) {
          clearInterval(intervalId);
          resolve(this.FB);
        } else if (pollNumber > pollAttempts) {
          reject(CONNECT_FAIL_MESSAGE);
        } else {
          pollNumber += 1;
        }
      }, 350);
    });
  }

  reInit() {
    if (this.FB && this.appId) {
      window.FB.init({
        appId: String(this.appId),
        xfbml: true,
        version: FB_SDK_VERSION,
      });
    }
  }

  init(appId: number) {
    return (dispatch: Dispatch<StoreState>) => {
      this.appId = appId;

      window.fbAsyncInit = () => {
        window.FB.init({
          appId: String(appId),
          xfbml: true,
          version: FB_SDK_VERSION,
        });
        this.FB = window.FB;

        const onChatLoaded = () => {
          if (this.FB) {
            dispatch({ type: this.ACTIONS.SET_CUSTOMER_CHAT_LOADED });
            // @ts-ignore
            this.FB.Event.unsubscribe('customerchat.load', onChatLoaded);
            this.chatLoaded = true;
          }
        };

        // @ts-ignore
        this.FB.Event.subscribe('customerchat.load', onChatLoaded);
        dispatch({ type: this.ACTIONS.SET_IS_INITIALIZED });
      };

      FacebookSDK.injectSDKIntoDom();
    };
  }

  requestPermissions(permissions: string[]) {
    return () => this.loginWhenReady(permissions, 'rerequest');
  }

  requestPermissionsWithManualFlowFallback(
    permissions: string[],
    queryArgs?: { [key: string]: string | undefined },
    redirectPath?: string,
  ) {
    return async () => {
      try {
        await this.getFacebookSDK(2);
      } catch (err) {
        return this.loginWithManualFlow(permissions, queryArgs, redirectPath);
      }

      return this.loginWhenReady(permissions, 'rerequest');
    };
  }

  requestPermissionsWithManualFlowFallbackIgConnect(
    permissions: string[],
    queryArgs?: { [key: string]: string | undefined },
    redirectPath?: string,
  ) {
    return async () => {
      try {
        await this.getFacebookSDK(2);
      } catch (err) {
        return this.loginManualFlowIgConnect({
          requiredPermissions: permissions,
          queryArgs,
          redirectPath,
        });
      }

      return this.loginIgConnectWhenReady(permissions, 'rerequest');
    };
  }

  async loginIgConnectWhenReady(
    requiredPermissions: string[],
    authType: FBLoginAuthType,
  ) {
    const FacebookSDK = await this.getFacebookSDK();

    return new Promise<{
      facebookToken: string;
      grantedPermissions: string[];
      requestedPermissions: string[];
    }>((resolve, reject) => {
      FacebookSDK.login(
        response => {
          console.log('FB login response: ', response);
          console.log(
            'FB login response stringified: ',
            JSON.stringify(response),
          );
          if (response.authResponse && response.status === 'connected') {
            const { accessToken, grantedScopes = '' } = response.authResponse;

            resolve({
              facebookToken: accessToken,
              grantedPermissions: grantedScopes.split(','),
              requestedPermissions: requiredPermissions,
            });
          } else {
            reject(response);
          }
        },
        {
          auth_type: authType,
          scope: requiredPermissions.join(),
          return_scopes: true,
          enable_profile_selector: true,
          // @ts-ignore
          extras: {
            setup: {
              channel: 'IG_API_ONBOARDING',
            },
          },
        },
      );
    });
  }

  async loginWhenReady(
    requiredPermissions: string[],
    authType: FBLoginAuthType,
  ) {
    const FacebookSDK = await this.getFacebookSDK();

    return new Promise<{
      facebookToken: string;
      grantedPermissions: string[];
      requestedPermissions: string[];
    }>((resolve, reject) => {
      FacebookSDK.login(
        response => {
          if (response.authResponse && response.status === 'connected') {
            const { accessToken, grantedScopes = '' } = response.authResponse;

            resolve({
              facebookToken: accessToken,
              grantedPermissions: grantedScopes.split(','),
              requestedPermissions: requiredPermissions,
            });
          } else {
            reject(response);
          }
        },
        {
          auth_type: authType,
          scope: requiredPermissions.join(),
          return_scopes: true,
          enable_profile_selector: true,
        },
      );
    });
  }

  async getAuthToken() {
    const FacebookSDK = await this.getFacebookSDK();
    return new Promise<string>((resolve, reject) => {
      FacebookSDK.getLoginStatus(response => {
        if (response.status !== 'connected') {
          return reject('');
        }
        resolve(response.authResponse.accessToken);
      });
    });
  }

  async loginWithFullReload(requiredPermissions: string[], authToken: string) {
    const FacebookSDK = await this.getFacebookSDK();

    return new Promise<{
      facebookToken: string;
      grantedPermissions: string[];
      requestedPermissions: string[];
    }>((resolve, reject) => {
      FacebookSDK.api(
        '/me/permissions',
        'delete',
        { auth_token: authToken },
        (response: any) => {
          if (!response || response.error) {
            return reject();
          }
          FacebookSDK.login(
            response => {
              if (response.authResponse && response.status === 'connected') {
                const { accessToken, grantedScopes = '' } =
                  response.authResponse;

                resolve({
                  facebookToken: accessToken,
                  grantedPermissions: grantedScopes.split(','),
                  requestedPermissions: requiredPermissions,
                });
              } else {
                reject();
              }
            },
            {
              scope: requiredPermissions.join(),
              return_scopes: true,
              enable_profile_selector: true,
            },
          );
        },
      );
    });
  }

  async loginWithManualFlow(
    requiredPermissions: string[],
    queryArgs?: { [key: string]: string | undefined },
    redirectPath?: string,
    extraOptions?: {
      authType?: FBLoginAuthType;
      state?: string;
    },
  ) {
    const { authType = 'rerequest', state } = extraOptions || {};
    const q = queryArgs ? queryString.stringify(queryArgs) : null;
    const loginUrl = `https://www.facebook.com/v14.0/dialog/oauth?${queryString.stringify(
      {
        client_id: this.appId,
        redirect_uri: `${window.location.origin}/${redirectPath || 'login'}${
          q === null ? '' : `?${q}`
        }`,
        response_type: 'token granted_scopes',
        auth_type: authType,
        state,
        scope: requiredPermissions.join(),
      },
    )}`;
    window.location.assign(loginUrl);
  }

  async loginManualFlowIgConnect({
    requiredPermissions,
    queryArgs,
    redirectPath,
    extraOptions = {},
  }: {
    requiredPermissions: string[];
    queryArgs?: { [key: string]: string | undefined };
    redirectPath?: string;
    extraOptions?: {
      authType?: FBLoginAuthType;
      state?: string;
    };
  }) {
    const q = queryArgs ? queryString.stringify(queryArgs) : null;
    const { authType = 'rerequest', state } = extraOptions;
    const loginUrl = `https://www.facebook.com/v14.0/dialog/oauth?${queryString.stringify(
      {
        client_id: this.appId,
        display: 'page',
        redirect_uri: `${window.location.origin}/${redirectPath || 'login'}${
          q === null ? '' : `?${q}`
        }`,
        extras: JSON.stringify({
          setup: {
            channel: 'IG_API_ONBOARDING',
          },
        }),
        response_type: 'token',
        scope: requiredPermissions.join(),
        state,
        auth_type: authType,
      },
    )}`;
    window.location.assign(loginUrl);
  }

  private static injectSDKIntoDom() {
    const id = 'facebook-jssdk';
    const facebookSDKExists = !!document.getElementById(id);
    const firstScript = document.getElementsByTagName('script')[0];

    if (!facebookSDKExists && firstScript) {
      const facebookSDKScript = document.createElement('script');
      facebookSDKScript.id = id;
      facebookSDKScript.src =
        '//connect.facebook.net/en_US/sdk/xfbml.customerchat.js';

      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(facebookSDKScript, firstScript);
      }
    }
  }
}

const facebookSDKActions = new FacebookSDK();

export default facebookSDKActions;

export const useFacebookSDKActions = getActionHookBuilder(facebookSDKActions, [
  'loginManualFlowIgConnect',
  'requestPermissionsWithManualFlowFallbackIgConnect',
  'requestPermissions',
  'requestPermissionsWithManualFlowFallback',
  'init',
]);
